/**
 * Control diario de planta (desinfección) — docs/DISENO_CONTROL_PLANTA.md.
 *
 * El operador registra el día desde el celular (terreno.); acá la oficina ve
 * el mes, revisa los días con cloro fuera de rango, carga o corrige un día,
 * completa los datos manuales del mes y descarga el informe (Excel / PDF).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FlaskConical,
  Pencil,
  Plus,
  Settings2,
} from "lucide-react";

import { ApiError } from "../../lib/api";
import { hasPermission } from "../../lib/auth";
import { useAuth } from "../auth/useAuth";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ErrorCarga,
  Input,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  crearPlanta,
  crearPozo,
  crearPunto,
  descargarInforme,
  editarPlanta,
  editarPozo,
  editarPunto,
  fetchConfigPlantas,
  fetchMes,
  guardarManual,
  guardarRangoCloro,
  guardarRegistro,
  revisarRegistro,
  type Planta,
  type RegistroInput,
  type RegistroPlanta,
  type ResumenMes,
  type TipoPunto,
} from "./plantasApi";

function mesActual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function num(v: string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? v : n.toLocaleString("es-CL", { maximumFractionDigits: 2 });
}

const ORIGEN: Record<string, string> = {
  manual: "manual",
  facturacion: "facturación",
  padron: "padrón",
  inventario: "inventario",
};

export function PlantasPanel(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const { state } = useAuth();
  const [plantaId, setPlantaId] = useState<number | null>(null);
  const [periodo, setPeriodo] = useState(mesActual());
  const [creando, setCreando] = useState(false);
  const [configurando, setConfigurando] = useState(false);
  const [editando, setEditando] = useState<{ fecha: string; registro: RegistroPlanta | null } | null>(
    null,
  );
  const [manualAbierto, setManualAbierto] = useState(false);

  const configQ = useQuery({ queryKey: ["plantas"], queryFn: fetchConfigPlantas });
  const plantas = configQ.data?.plantas ?? [];
  useEffect(() => {
    if (plantaId === null && plantas.length > 0) setPlantaId(plantas[0]!.id);
  }, [plantas, plantaId]);
  const planta = plantas.find((p) => p.id === plantaId) ?? null;
  const [anio, mes] = periodo.split("-").map(Number) as [number, number];

  const mesQ = useQuery({
    queryKey: ["planta-mes", plantaId, anio, mes],
    queryFn: () => fetchMes(plantaId as number, anio, mes),
    enabled: plantaId !== null && !!anio && !!mes,
  });

  const revisar = useMutation({
    mutationFn: revisarRegistro,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["planta-mes"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Día revisado");
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo revisar"),
  });
  const descargar = useMutation({
    mutationFn: (formato: "xlsx" | "pdf") =>
      descargarInforme(plantaId as number, anio, mes, formato),
    onError: () => toast.error("No se pudo descargar el informe"),
  });

  if (configQ.isLoading) return <Cargando />;
  if (configQ.isError)
    return <ErrorCarga error={configQ.error} onReintentar={() => configQ.refetch()} />;

  if (plantas.length === 0) {
    return (
      <div className="space-y-3 rounded-lg border border-dashed border-neutral-300 bg-white p-8 text-center">
        <FlaskConical className="mx-auto h-8 w-8 text-brand-600" strokeWidth={1.5} />
        <p className="text-sm text-neutral-600">
          Configura la planta del comité para llevar el control diario de desinfección (cloro
          residual, caudal, electricidad y niveles de pozo) y generar el informe mensual.
        </p>
        <Button icon={Plus} onClick={() => setCreando(true)}>
          Crear planta
        </Button>
        {creando && <CrearPlantaModal onClose={() => setCreando(false)} onCreada={setPlantaId} />}
      </div>
    );
  }

  const res = mesQ.data;
  const puntos = planta ? planta.puntos.filter((p) => p.activo) : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Planta"
          value={plantaId ?? ""}
          onChange={(e) => setPlantaId(Number(e.target.value))}
          containerClassName="w-56"
        >
          {plantas.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
              {p.activa ? "" : " (inactiva)"}
            </option>
          ))}
        </Select>
        <Input
          label="Mes"
          type="month"
          value={periodo}
          onChange={(e) => setPeriodo(e.target.value)}
          containerClassName="w-44"
        />
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="secondary" icon={Settings2} onClick={() => setConfigurando(true)}>
            Configurar
          </Button>
          <Button variant="secondary" icon={Plus} onClick={() => setCreando(true)}>
            Planta
          </Button>
          <Button
            icon={Pencil}
            onClick={() =>
              setEditando({ fecha: new Date().toISOString().slice(0, 10), registro: null })
            }
          >
            Registrar día
          </Button>
        </div>
      </div>

      {configQ.data && (
        <p className="text-xs text-neutral-500">
          Rango de cloro libre residual del comité:{" "}
          <b>
            {num(configQ.data.cloro_min_mg_l)} – {num(configQ.data.cloro_max_mg_l)} mg/L
          </b>{" "}
          (NCh 409). Fuera de rango, el operador anota la acción correctiva y el día queda para
          revisión.
        </p>
      )}

      {res && <ResumenCard res={res} onManual={() => setManualAbierto(true)} onDescargar={(f) => descargar.mutate(f)} descargando={descargar.isPending} />}

      <TableWrap label="Controles de planta">
        <table className="min-w-full text-xs">
          <thead className="bg-neutral-50 text-left text-neutral-500">
            <tr>
              <th className="px-3 py-2">Día</th>
              {puntos.map((p) => (
                <th key={p.id} className="px-3 py-2">
                  {p.nombre}
                  <span className="block font-normal text-neutral-500">mg/L</span>
                </th>
              ))}
              <th className="px-3 py-2">Producción m³</th>
              <th className="px-3 py-2">Energía kWh</th>
              <th className="px-3 py-2">Operador</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {mesQ.isLoading && (
              <tr>
                <td className="px-3" colSpan={puntos.length + 6}>
                  <Cargando inline />
                </td>
              </tr>
            )}
            {res && res.registros.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-neutral-500" colSpan={puntos.length + 6}>
                  Sin registros este mes. El operador los ingresa desde la app de terreno.
                </td>
              </tr>
            )}
            {res?.registros.map((r) => {
              const principal = new Map(
                r.mediciones.filter((m) => !m.extra).map((m) => [m.punto_id, m]),
              );
              return (
                <tr key={r.id} className={r.fuera_de_rango && !r.revisado_at ? "bg-rose-50/60" : ""}>
                  <td className="px-3 py-2 font-medium text-neutral-700">
                    {r.fecha.slice(8, 10)}-{r.fecha.slice(5, 7)}
                  </td>
                  {puntos.map((p) => {
                    const m = principal.get(p.id);
                    return (
                      <td
                        key={p.id}
                        className={`px-3 py-2 ${m?.fuera_de_rango ? "font-bold text-rose-700" : "text-neutral-700"}`}
                        title={m?.lugar ?? ""}
                      >
                        {m ? num(m.cloro_mg_l) : "—"}
                        {m?.lugar && <span className="block text-xs text-neutral-500">{m.lugar}</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2">{num(r.produccion_m3)}</td>
                  <td className="px-3 py-2">{num(r.energia_kwh)}</td>
                  <td className="px-3 py-2 text-neutral-500">{r.operador_nombre ?? "—"}</td>
                  <td className="px-3 py-2">
                    {r.fuera_de_rango ? (
                      r.revisado_at ? (
                        <Badge tone="slate">Revisado</Badge>
                      ) : (
                        <span title={r.accion_correctiva ?? ""}>
                          <Badge tone="rose">Fuera de rango</Badge>
                        </span>
                      )
                    ) : (
                      <Badge tone="emerald">OK</Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {r.fuera_de_rango && !r.revisado_at && (
                      <Button
                        size="sm"
                        variant="link"
                        icon={CheckCircle2}
                        onClick={() => revisar.mutate(r.id)}
                        title={r.accion_correctiva ?? ""}
                      >
                        Revisar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="link"
                      icon={Pencil}
                      onClick={() => setEditando({ fecha: r.fecha, registro: r })}
                    >
                      Editar
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableWrap>

      {creando && <CrearPlantaModal onClose={() => setCreando(false)} onCreada={setPlantaId} />}
      {configurando && planta && (
        <ConfigPlantaModal
          planta={planta}
          rango={{ min: configQ.data?.cloro_min_mg_l ?? "0.2", max: configQ.data?.cloro_max_mg_l ?? "2.0" }}
          puedeRango={hasPermission(state, "empresa.configurar")}
          onClose={() => setConfigurando(false)}
        />
      )}
      {editando && planta && (
        <RegistroDiaModal
          planta={planta}
          fechaInicial={editando.fecha}
          registro={editando.registro}
          onClose={() => setEditando(null)}
        />
      )}
      {manualAbierto && res && planta && (
        <ManualModal res={res} plantaId={planta.id} onClose={() => setManualAbierto(false)} />
      )}
    </div>
  );
}

function ResumenCard({
  res,
  onManual,
  onDescargar,
  descargando,
}: {
  res: ResumenMes;
  onManual: () => void;
  onDescargar: (f: "xlsx" | "pdf") => void;
  descargando: boolean;
}): JSX.Element {
  const dato = (etiqueta: string, valor: string | null, origen?: string | null) => (
    <div className="rounded-md border border-neutral-100 bg-neutral-50 px-3 py-2">
      <div className="text-xs text-neutral-500">{etiqueta}</div>
      <div className="text-sm font-semibold text-neutral-800">{valor ?? "—"}</div>
      {origen !== undefined && (
        <div className="text-xs text-neutral-500">{origen ? ORIGEN[origen] ?? origen : "falta ingresar"}</div>
      )}
    </div>
  );
  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <FileSpreadsheet className="h-4 w-4 text-brand-600" />
        <span className="text-sm font-semibold text-neutral-800">
          Resumen del mes · {res.dias_registrados} día(s) registrados
        </span>
        {res.dias_fuera_de_rango > 0 && (
          <Badge tone="rose">
            <AlertTriangle className="mr-1 inline h-3 w-3" />
            {res.dias_fuera_de_rango} fuera de rango
          </Badge>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={onManual}>
            Datos del mes
          </Button>
          <Button size="sm" variant="secondary" icon={Download} disabled={descargando} onClick={() => onDescargar("xlsx")}>
            Excel
          </Button>
          <Button size="sm" icon={Download} disabled={descargando} onClick={() => onDescargar("pdf")}>
            PDF
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {dato("Agua producida (m³)", num(res.produccion_m3))}
        {dato("Agua facturada (m³)", res.agua_facturada.valor, res.agua_facturada.origen)}
        {dato("Energía (kWh)", num(res.energia_kwh))}
        {dato("Arranques", res.arranques.valor, res.arranques.origen)}
        {dato("Gasto de cloro", res.gasto_cloro.valor, res.gasto_cloro.origen)}
        {dato("Nivel dinámico prom. (m)", num(res.nivel_dinamico_prom_m))}
        {dato("Nivel estático prom. (m)", num(res.nivel_estatico_prom_m))}
      </div>
    </Card>
  );
}

function CrearPlantaModal({
  onClose,
  onCreada,
}: {
  onClose: () => void;
  onCreada: (id: number) => void;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [nombre, setNombre] = useState("Planta N° 1");
  const [pozos, setPozos] = useState("Pozo 1");
  const [red, setRed] = useState(2);
  const [electricidad, setElectricidad] = useState(true);
  const [dosificador, setDosificador] = useState(true);
  const crear = useMutation({
    mutationFn: () =>
      crearPlanta({
        nombre,
        tiene_electricidad: electricidad,
        tiene_dosificador: dosificador,
        pozos: pozos
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean)
          .map((p) => ({ nombre: p })),
        puntos: [
          { nombre: "Planta", tipo: "planta" as TipoPunto },
          ...Array.from({ length: red }, (_, i) => ({ nombre: `Red ${i + 1}`, tipo: "red" as TipoPunto })),
        ],
      }),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ["plantas"] });
      toast.success("Planta creada");
      onCreada(p.id);
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo crear"),
  });
  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([nombre, pozos, red, electricidad, dosificador]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crear.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva planta"
      icon={FlaskConical}
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => crear.mutate()} disabled={crear.isPending || !nombre.trim()}>
            Crear
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        <Input
          label="Pozos (separados por coma)"
          value={pozos}
          onChange={(e) => setPozos(e.target.value)}
          hint="Cada pozo registra caudal, horómetro y niveles; se ajusta después en Configurar."
        />
        <Input
          label="Puntos de medición en la red"
          type="number"
          min={0}
          max={10}
          value={red}
          onChange={(e) => setRed(Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
          hint="Además de la medición en la planta."
        />
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input type="checkbox" checked={electricidad} onChange={(e) => setElectricidad(e.target.checked)} />
          Registra medidor de electricidad
        </label>
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input type="checkbox" checked={dosificador} onChange={(e) => setDosificador(e.target.checked)} />
          Tiene dosificador clorador
        </label>
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

function ConfigPlantaModal({
  planta,
  rango,
  puedeRango,
  onClose,
}: {
  planta: Planta;
  rango: { min: string; max: string };
  puedeRango: boolean;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [nuevoPozo, setNuevoPozo] = useState("");
  const [nuevoPunto, setNuevoPunto] = useState("");
  const [tipoPunto, setTipoPunto] = useState<TipoPunto>("red");
  const [min, setMin] = useState(rango.min);
  const [max, setMax] = useState(rango.max);
  const refrescar = () => void qc.invalidateQueries({ queryKey: ["plantas"] });
  const onError = (e: unknown) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar");

  const planta_ = useMutation({
    mutationFn: (body: Parameters<typeof editarPlanta>[1]) => editarPlanta(planta.id, body),
    onSuccess: refrescar,
    onError,
  });
  const pozoNuevo = useMutation({
    mutationFn: () => crearPozo(planta.id, nuevoPozo.trim()),
    onSuccess: () => {
      setNuevoPozo("");
      refrescar();
    },
    onError,
  });
  const pozo = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof editarPozo>[1] }) =>
      editarPozo(id, body),
    onSuccess: refrescar,
    onError,
  });
  const puntoNuevo = useMutation({
    mutationFn: () => crearPunto(planta.id, nuevoPunto.trim(), tipoPunto),
    onSuccess: () => {
      setNuevoPunto("");
      refrescar();
    },
    onError,
  });
  const punto = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof editarPunto>[1] }) =>
      editarPunto(id, body),
    onSuccess: refrescar,
    onError,
  });
  const rangoMut = useMutation({
    mutationFn: () => guardarRangoCloro(min, max),
    onSuccess: () => {
      refrescar();
      toast.success("Rango de cloro guardado");
    },
    onError,
  });

  const casilla = (checked: boolean, onChange: (v: boolean) => void, texto: string) => (
    <label className="inline-flex items-center gap-1 text-xs text-neutral-600">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {texto}
    </label>
  );

  return (
    <Modal open onClose={onClose} title={`Configurar ${planta.nombre}`} icon={Settings2} size="lg">
      <div className="space-y-5 text-sm">
        <section className="space-y-2">
          <h4 className="font-semibold text-neutral-800">Planta</h4>
          <div className="flex flex-wrap gap-4">
            {casilla(planta.tiene_electricidad, (v) => planta_.mutate({ tiene_electricidad: v }), "Medidor de electricidad")}
            {casilla(planta.tiene_dosificador, (v) => planta_.mutate({ tiene_dosificador: v }), "Dosificador clorador")}
            {casilla(planta.activa, (v) => planta_.mutate({ activa: v }), "Activa")}
          </div>
        </section>

        <section className="space-y-2">
          <h4 className="font-semibold text-neutral-800">Puntos de medición de cloro</h4>
          {planta.puntos.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 rounded border border-neutral-100 px-3 py-2">
              <span className={`min-w-0 flex-1 ${p.activo ? "" : "text-neutral-500 line-through"}`}>
                {p.nombre} <span className="text-xs text-neutral-500">({p.tipo === "planta" ? "planta" : "red"})</span>
              </span>
              {casilla(p.activo, (v) => punto.mutate({ id: p.id, body: { activo: v } }), "Activo")}
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-2">
            <Input label="Nuevo punto" value={nuevoPunto} onChange={(e) => setNuevoPunto(e.target.value)} placeholder="Red 3 · Sede social" containerClassName="flex-1" />
            <Select label="Tipo" value={tipoPunto} onChange={(e) => setTipoPunto(e.target.value as TipoPunto)} containerClassName="w-32">
              <option value="red">Red</option>
              <option value="planta">Planta</option>
            </Select>
            <Button icon={Plus} variant="secondary" disabled={!nuevoPunto.trim()} onClick={() => puntoNuevo.mutate()}>
              Agregar
            </Button>
          </div>
        </section>

        <section className="space-y-2">
          <h4 className="font-semibold text-neutral-800">Pozos</h4>
          {planta.pozos.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-3 rounded border border-neutral-100 px-3 py-2">
              <span className={`min-w-0 flex-1 ${p.activo ? "" : "text-neutral-500 line-through"}`}>{p.nombre}</span>
              {casilla(p.registra_caudal, (v) => pozo.mutate({ id: p.id, body: { registra_caudal: v } }), "Caudal")}
              {casilla(p.registra_horometro, (v) => pozo.mutate({ id: p.id, body: { registra_horometro: v } }), "Horómetro")}
              {casilla(p.registra_niveles, (v) => pozo.mutate({ id: p.id, body: { registra_niveles: v } }), "Niveles")}
              {casilla(p.activo, (v) => pozo.mutate({ id: p.id, body: { activo: v } }), "Activo")}
            </div>
          ))}
          <div className="flex items-end gap-2">
            <Input label="Nuevo pozo" value={nuevoPozo} onChange={(e) => setNuevoPozo(e.target.value)} placeholder="Pozo 2" containerClassName="flex-1" />
            <Button icon={Plus} variant="secondary" disabled={!nuevoPozo.trim()} onClick={() => pozoNuevo.mutate()}>
              Agregar
            </Button>
          </div>
        </section>

        {puedeRango && (
          <section className="space-y-2">
            <h4 className="font-semibold text-neutral-800">Rango de cloro libre residual (mg/L)</h4>
            <div className="flex items-end gap-2">
              <Input label="Mínimo" type="number" step="0.1" min={0} value={min} onChange={(e) => setMin(e.target.value)} containerClassName="w-28" />
              <Input label="Máximo" type="number" step="0.1" min={0} value={max} onChange={(e) => setMax(e.target.value)} containerClassName="w-28" />
              <Button variant="secondary" onClick={() => rangoMut.mutate()} disabled={rangoMut.isPending}>
                Guardar rango
              </Button>
            </div>
            <p className="text-xs text-neutral-500">NCh 409: 0,2 – 2,0 mg/L. Ajústalo si la SISS le pidió otro al comité.</p>
          </section>
        )}
      </div>
    </Modal>
  );
}

function RegistroDiaModal({
  planta,
  fechaInicial,
  registro,
  onClose,
}: {
  planta: Planta;
  fechaInicial: string;
  registro: RegistroPlanta | null;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const puntos = planta.puntos.filter((p) => p.activo);
  const pozos = planta.pozos.filter((p) => p.activo);
  const [fecha, setFecha] = useState(fechaInicial);
  const [operador, setOperador] = useState(registro?.operador_nombre ?? "");
  const [kwh, setKwh] = useState(registro?.electricidad_kwh ?? "");
  const [frec, setFrec] = useState(registro?.dosificador_frecuencia ?? "");
  const [desp, setDesp] = useState(registro?.dosificador_desplazamiento ?? "");
  const [obs, setObs] = useState(registro?.observaciones ?? "");
  const [accion, setAccion] = useState(registro?.accion_correctiva ?? "");
  const [med, setMed] = useState<Record<number, { hora: string; cloro: string; lugar: string }>>(() =>
    Object.fromEntries(
      puntos.map((p) => {
        const m = registro?.mediciones.find((x) => x.punto_id === p.id && !x.extra);
        return [p.id, { hora: m?.hora?.slice(0, 5) ?? "", cloro: m?.cloro_mg_l ?? "", lugar: m?.lugar ?? "" }];
      }),
    ),
  );
  const [lec, setLec] = useState<Record<number, { caudal: string; horo: string; est: string; din: string }>>(() =>
    Object.fromEntries(
      pozos.map((p) => {
        const l = registro?.lecturas.find((x) => x.pozo_id === p.id);
        return [
          p.id,
          {
            caudal: l?.caudal_m3 ?? "",
            horo: l?.horometro_h ?? "",
            est: l?.nivel_estatico_m ?? "",
            din: l?.nivel_dinamico_m ?? "",
          },
        ];
      }),
    ),
  );

  const vacio = (v: string) => (v.trim() === "" ? null : v.trim().replace(",", "."));
  const guardar = useMutation({
    mutationFn: () => {
      const body: RegistroInput = {
        operador_nombre: vacio(operador),
        electricidad_kwh: planta.tiene_electricidad ? vacio(kwh) : null,
        dosificador_frecuencia: planta.tiene_dosificador ? vacio(frec) : null,
        dosificador_desplazamiento: planta.tiene_dosificador ? vacio(desp) : null,
        observaciones: vacio(obs),
        accion_correctiva: vacio(accion),
        mediciones: puntos
          .filter((p) => med[p.id]?.cloro.trim())
          .map((p) => ({
            punto_id: p.id,
            hora: med[p.id]!.hora || null,
            cloro_mg_l: med[p.id]!.cloro.replace(",", "."),
            lugar: vacio(med[p.id]!.lugar),
            extra: false,
          })),
        lecturas: pozos.map((p) => ({
          pozo_id: p.id,
          caudal_m3: vacio(lec[p.id]!.caudal),
          horometro_h: vacio(lec[p.id]!.horo),
          nivel_estatico_m: vacio(lec[p.id]!.est),
          nivel_dinamico_m: vacio(lec[p.id]!.din),
        })),
      };
      // Mediciones extra del registro original se conservan al editar desde la oficina.
      for (const m of registro?.mediciones.filter((x) => x.extra) ?? []) {
        body.mediciones.push({ punto_id: m.punto_id, hora: m.hora, cloro_mg_l: m.cloro_mg_l, lugar: m.lugar, extra: true });
      }
      return guardarRegistro(planta.id, fecha, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["planta-mes"] });
      toast.success("Día guardado");
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar"),
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([fecha, operador, kwh, frec, desp, obs, accion, med, lec]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    guardar.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      closeOnBackdrop={false}
      title={registro ? "Editar día" : "Registrar día"}
      subtitle={planta.nombre}
      icon={FlaskConical}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} disabled={guardar.isPending || !fecha}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} disabled={!!registro} />
          <Input label="Operador" value={operador} onChange={(e) => setOperador(e.target.value)} />
        </div>
        <div>
          <div className="mb-1 text-sm font-semibold text-neutral-800">Cloro residual (mg/L)</div>
          <div className="space-y-2">
            {puntos.map((p) => (
              <div key={p.id} className="grid grid-cols-[1fr_90px_90px_1fr] items-end gap-2">
                <span className="pb-2 text-sm text-neutral-700">{p.nombre}</span>
                <Input label="Hora" type="time" value={med[p.id]!.hora} onChange={(e) => setMed({ ...med, [p.id]: { ...med[p.id]!, hora: e.target.value } })} />
                <Input label="mg/L" inputMode="decimal" value={med[p.id]!.cloro} onChange={(e) => setMed({ ...med, [p.id]: { ...med[p.id]!, cloro: e.target.value } })} />
                {p.tipo === "red" ? (
                  <Input label="Socio / casa" value={med[p.id]!.lugar} onChange={(e) => setMed({ ...med, [p.id]: { ...med[p.id]!, lugar: e.target.value } })} />
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
        </div>
        {pozos.map((p) => (
          <div key={p.id}>
            <div className="mb-1 text-sm font-semibold text-neutral-800">{p.nombre}</div>
            <div className="grid grid-cols-4 gap-2">
              {p.registra_caudal && <Input label="Caudal (m³)" inputMode="decimal" value={lec[p.id]!.caudal} onChange={(e) => setLec({ ...lec, [p.id]: { ...lec[p.id]!, caudal: e.target.value } })} />}
              {p.registra_horometro && <Input label="Horómetro" inputMode="decimal" value={lec[p.id]!.horo} onChange={(e) => setLec({ ...lec, [p.id]: { ...lec[p.id]!, horo: e.target.value } })} />}
              {p.registra_niveles && <Input label="Nivel estático" inputMode="decimal" value={lec[p.id]!.est} onChange={(e) => setLec({ ...lec, [p.id]: { ...lec[p.id]!, est: e.target.value } })} />}
              {p.registra_niveles && <Input label="Nivel dinámico" inputMode="decimal" value={lec[p.id]!.din} onChange={(e) => setLec({ ...lec, [p.id]: { ...lec[p.id]!, din: e.target.value } })} />}
            </div>
          </div>
        ))}
        <div className="grid grid-cols-3 gap-2">
          {planta.tiene_electricidad && <Input label="Electricidad (kWh)" inputMode="decimal" value={kwh} onChange={(e) => setKwh(e.target.value)} />}
          {planta.tiene_dosificador && <Input label="Dosificador frec." value={frec} onChange={(e) => setFrec(e.target.value)} />}
          {planta.tiene_dosificador && <Input label="Dosificador desp." value={desp} onChange={(e) => setDesp(e.target.value)} />}
        </div>
        <Textarea label="Acción correctiva (obligatoria si hay cloro fuera de rango)" value={accion} onChange={(e) => setAccion(e.target.value)} rows={2} />
        <Textarea label="Observaciones" value={obs} onChange={(e) => setObs(e.target.value)} rows={2} />
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

function ManualModal({
  res,
  plantaId,
  onClose,
}: {
  res: ResumenMes;
  plantaId: number;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [cloro, setCloro] = useState(res.manual.gasto_cloro_kg ?? "");
  const [facturada, setFacturada] = useState(res.manual.agua_facturada_m3 ?? "");
  const [arranques, setArranques] = useState(res.manual.arranques?.toString() ?? "");
  const [obs, setObs] = useState(res.manual.observaciones ?? "");
  const guardar = useMutation({
    mutationFn: () =>
      guardarManual(plantaId, res.anio, res.mes, {
        gasto_cloro_kg: cloro.trim() ? cloro.replace(",", ".") : null,
        agua_facturada_m3: facturada.trim() ? facturada.replace(",", ".") : null,
        arranques: arranques.trim() ? Number(arranques) : null,
        observaciones: obs.trim() || null,
      }),
    onSuccess: (r) => {
      qc.setQueryData(["planta-mes", plantaId, res.anio, res.mes], r);
      toast.success("Datos del mes guardados");
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar"),
  });
  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([cloro, facturada, arranques, obs]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    guardar.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Datos del mes"
      subtitle="Lo que no sale del registro diario. En blanco = se usa el automático."
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => guardar.mutate()} disabled={guardar.isPending}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Gasto mensual de cloro (kg)" inputMode="decimal" value={cloro} onChange={(e) => setCloro(e.target.value)} hint={res.gasto_cloro.origen === "inventario" ? `Inventario: ${res.gasto_cloro.valor}` : "Si registran las salidas de cloro en bodega, se calcula solo."} />
        <Input label="Agua mensual facturada (m³)" inputMode="decimal" value={facturada} onChange={(e) => setFacturada(e.target.value)} hint={res.agua_facturada.origen === "facturacion" ? `Facturación: ${res.agua_facturada.valor}` : undefined} />
        <Input label="Número total de arranques" inputMode="numeric" value={arranques} onChange={(e) => setArranques(e.target.value)} hint={res.arranques.origen === "padron" ? `Padrón: ${res.arranques.valor}` : undefined} />
        <Textarea label="Observaciones del mes" value={obs} onChange={(e) => setObs(e.target.value)} rows={3} />
      </div>
      {dialogoDescarte}
    </Modal>
  );
}
